import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useProgressionContext } from '../context/ProgressionContext'
import { completeAssessment } from '../services/api'
import type { FnaFormData } from '../types'
import { humanizeKey } from '../utils/labels'
import '../styles/progression.css'

/** Fields the Pydantic schema expects as integers. */
const INT_FIELDS = new Set([
  'age', 'spouseAge', 'retireAge', 'numChildren',
])

/** Fields the Pydantic schema expects as floats. */
const FLOAT_FIELDS = new Set([
  'annualIncome', 'spouseAnnualIncome', 'otherMonthlyIncome', 'spouseOtherMonthlyIncome',
  'totalRetirementSavings', 'monthlyRetirementContribution', 'insuredAmount',
  'clientMonthlyContribution', 'spouseMonthlyContribution',
  'clientEmployerContribution', 'spouseEmployerContribution',
  'otherAssets', 'totalNonRetirementSavings',
  'clientNonRetirementSavings', 'spouseNonRetirementSavings', 'jointNonRetirementSavings',
  'clientMonthlySavings', 'spouseMonthlySavings', 'jointMonthlySavings',
  'clientRetirement', 'spouseRetirement', 'jointRetirement', 'amountEstate', 'monthlyAmount',
])

/**
 * Coerces string values back to their expected numeric types before submission.
 * Input elements always produce strings; this restores numbers for the backend.
 *
 * @param data - The raw form data with potentially stringified numbers
 * @returns FnaFormData with numeric fields coerced to numbers
 */
function coerceFormDataTypes(data: FnaFormData): FnaFormData {
  const result: FnaFormData = { ...data }
  for (const [key, value] of Object.entries(result)) {
    if (typeof value !== 'string') continue
    if (INT_FIELDS.has(key)) {
      const parsed = parseInt(value, 10)
      if (!isNaN(parsed)) result[key] = parsed
    } else if (FLOAT_FIELDS.has(key)) {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) result[key] = parsed
    }
  }
  return result
}

/**
 * Flattens form data into a list of key-value pairs for display.
 * Skips complex nested objects and arrays (shown separately).
 *
 * @param data - The form data to flatten
 * @returns Array of [key, displayLabel, value] tuples
 */
function buildDisplayItems(data: FnaFormData): Array<[string, string, string]> {
  const items: Array<[string, string, string]> = []

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue
    if (Array.isArray(value)) continue
    if (typeof value === 'object') continue

    items.push([key, humanizeKey(key), String(value)])
  }

  return items
}

/**
 * Data correction page for reviewing and editing captured form data.
 * Users can modify values before saving the assessment and continuing to Gemini Live.
 *
 * @returns JSX.Element The data correction page
 */
const DataCorrectionPage: React.FC = () => {
  const navigate = useNavigate()
  const { formData, setFormData, completeStep } = useProgressionContext()

  const [localData, setLocalData] = useState<FnaFormData>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize local data from context or localStorage
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setLocalData(formData)
    } else {
      try {
        const stored = localStorage.getItem('form_data')
        if (stored) {
          setLocalData(JSON.parse(stored))
        }
      } catch (err) {
        console.error({
          message: 'Error loading form_data from localStorage',
          error: err instanceof Error ? err.message : String(err),
          context: { action: 'DataCorrectionPage.init' },
        })
      }
    }
  }, [formData])

  const displayItems = useMemo(() => buildDisplayItems(localData), [localData])

  // Get array fields for separate display
  const arrayFields = useMemo(() => {
    const fields: Array<[string, string, unknown[]]> = []
    for (const [key, value] of Object.entries(localData)) {
      if (Array.isArray(value) && value.length > 0) {
        fields.push([key, humanizeKey(key), value])
      }
    }
    return fields
  }, [localData])

  const handleValueChange = (key: string, newValue: string) => {
    setLocalData(prev => ({ ...prev, [key]: newValue }))
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      setError(null)

      // Coerce string values to expected numeric types before submission
      const coerced = coerceFormDataTypes(localData)

      // Save corrected data back to context
      setFormData(coerced)

      // Persist the completed assessment and cache summary context for Claude compatibility.
      const completion = await completeAssessment(coerced)
      try {
        if (completion.summary) {
          localStorage.setItem('results_summary', JSON.stringify(completion.summary))
        } else {
          localStorage.removeItem('results_summary')
        }
      } catch (storageError) {
        console.error({
          message: 'Error persisting results_summary after assessment completion',
          error: storageError instanceof Error ? storageError.message : String(storageError),
          context: { action: 'DataCorrectionPage.handleSubmit.cacheSummary' },
        })
      }
      completeStep(3)
      navigate('/userquestionsgemini')
    } catch (err) {
      console.error({
        message: 'Error saving assessment data',
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        context: { action: 'handleSubmit' },
      })

      // Extract specific error message from backend response
      let errorMessage = 'Failed to save your assessment. Please try again.'
      if (err instanceof AxiosError && err.response?.data) {
        const data = err.response.data as Record<string, unknown>
        if (data.field_errors && Array.isArray(data.field_errors)) {
          errorMessage = `Validation errors: ${(data.field_errors as string[]).join(', ')}`
        } else if (data.error) {
          errorMessage = `Assessment save failed: ${data.error}`
        }
      } else if (err instanceof Error) {
        errorMessage = `Assessment save failed: ${err.message}`
      }
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasData = displayItems.length > 0 || arrayFields.length > 0

  if (isSubmitting) {
    return (
      <div className="data-correction-page">
        <div className="data-correction-header">
          <h1>Saving Your Assessment</h1>
        </div>
        <div className="submitting-overlay">
          <div className="loading-spinner" />
          <p>Saving your financial information...</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            This may take a moment
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="data-correction-page">
      <div className="data-correction-header">
        <h1>Review Your Information</h1>
        <p className="home-subtitle">
          Please review the data captured during your conversation and make any corrections
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">!</span>
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-error">&times;</button>
        </div>
      )}

      {!hasData ? (
        <div className="data-correction-empty">
          <h3>No Data to Review</h3>
          <p>Complete the conversation first to capture your financial information.</p>
          <Link to="/conversation" className="btn btn-primary">
            Start Conversation
          </Link>
        </div>
      ) : (
        <>
          {/* Scalar fields */}
          <div className="data-correction-list">
            {displayItems.map(([key, label, value]) => (
              <div key={key} className="data-correction-item">
                <label className="data-correction-label" htmlFor={`field-${key}`}>
                  {label}
                </label>
                <input
                  id={`field-${key}`}
                  className="data-correction-value"
                  value={value}
                  onChange={(e) => handleValueChange(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* Array fields (debts, assets, etc.) */}
          {arrayFields.map(([key, label, items]) => (
            <div key={key} className="data-correction-list">
              <h3 style={{ color: 'var(--accent-cyan)', marginTop: 0 }}>{label}</h3>
              {items.map((item, idx) => (
                <div key={idx} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
                  {typeof item === 'object' && item !== null ? (
                    Object.entries(item).map(([subKey, subValue]) => (
                      <div key={subKey} className="data-correction-item">
                        <label className="data-correction-label">
                          {humanizeKey(subKey)}
                        </label>
                        <input
                          className="data-correction-value"
                          value={String(subValue ?? '')}
                          readOnly
                        />
                      </div>
                    ))
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>{String(item)}</span>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Action buttons */}
          <div className="data-correction-actions">
            <Link to="/conversation" className="btn btn-secondary">
              Back to Conversation
            </Link>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              Confirm &amp; Continue
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default DataCorrectionPage
